import fetchWithJWT from '../api/fetchWithJWT';

/**
 * Inventory Notification Service - Functional Implementation
 * Monitors product stock levels and alerts when inventory is low
 */

// Private state using closures
const state = {
  pollingIntervals: new Map(),
  lastStockAlerts: new Map(),
  callbacks: new Map()
};

/**
 * Check inventory levels and generate alerts
 * @private
 */
const checkInventory = async (businessId, onStockAlert, isInitial = false) => {
  try {
    const response = await fetchWithJWT(
      `/gift/list?business_id=${businessId}`
    );
    const data = await response.json();

    if (data.status === 'success' && data.result && Array.isArray(data.result)) {
      const products = data.result;
      const currentAlerts = [];

      // Check each product's stock level
      products.forEach(product => {
        const available = product.quantity - product.sold_quantity;
        
        if (available <= 0) {
          // Out of stock - Critical
          currentAlerts.push({
            id: `stock_${product._id}`,
            type: 'out_of_stock',
            severity: 'critical',
            productId: product._id,
            productName: product.name,
            available: 0,
            quantity: product.quantity,
            soldQuantity: product.sold_quantity,
            message: `Out of stock: ${product.name}`,
            timestamp: new Date().toISOString()
          });
        } else if (available === 1) {
          // Only 1 left - Critical
          currentAlerts.push({
            id: `stock_${product._id}`,
            type: 'critical_stock',
            severity: 'critical',
            productId: product._id,
            productName: product.name,
            available: 1,
            quantity: product.quantity,
            soldQuantity: product.sold_quantity,
            message: `Critical: Only 1 ${product.name} left!`,
            timestamp: new Date().toISOString()
          });
        } else if (available <= 3) {
          // Low stock - Warning
          currentAlerts.push({
            id: `stock_${product._id}`,
            type: 'low_stock',
            severity: 'warning',
            productId: product._id,
            productName: product.name,
            available,
            quantity: product.quantity,
            soldQuantity: product.sold_quantity,
            message: `Low stock: ${available} ${product.name} remaining`,
            timestamp: new Date().toISOString()
          });
        }
      });

      // Get previous alerts
      const previousAlerts = state.lastStockAlerts.get(businessId) || [];
      const previousAlertIds = new Set(previousAlerts.map(a => a.id));

      // Update stored state
      state.lastStockAlerts.set(businessId, currentAlerts);

      // On initial load, just update UI state without toast notifications
      if (isInitial && currentAlerts.length > 0 && onStockAlert) {
        onStockAlert({
          alerts: currentAlerts,
          newAlerts: [], // No new alerts on initial load (no toast)
          totalAlerts: currentAlerts.length,
          criticalCount: currentAlerts.filter(a => a.severity === 'critical').length,
          warningCount: currentAlerts.filter(a => a.severity === 'warning').length,
          timestamp: new Date().toISOString()
        });
      }

      // Find new alerts (skip on initial load)
      if (!isInitial && currentAlerts.length > 0) {
        const newAlerts = currentAlerts.filter(alert => !previousAlertIds.has(alert.id));
        
        // Also check for severity changes
        const updatedAlerts = currentAlerts.filter(alert => {
          const previous = previousAlerts.find(p => p.id === alert.id);
          return previous && previous.severity !== alert.severity;
        });

        const alertsToNotify = [...newAlerts, ...updatedAlerts];

        if (alertsToNotify.length > 0 && onStockAlert) {
          onStockAlert({
            alerts: currentAlerts,
            newAlerts: alertsToNotify,
            totalAlerts: currentAlerts.length,
            criticalCount: currentAlerts.filter(a => a.severity === 'critical').length,
            warningCount: currentAlerts.filter(a => a.severity === 'warning').length,
            timestamp: new Date().toISOString()
          });
        }
      }

      return currentAlerts;
    }
  } catch (error) {
    console.error('[InventoryNotificationService] Error checking inventory:', error);
    return [];
  }
};

/**
 * Start polling for inventory alerts
 * @param {string} businessId - Business ID to monitor
 * @param {function} onStockAlert - Callback when stock alerts detected
 * @param {number} interval - Polling interval in milliseconds (default: 30000 = 30 seconds)
 * @returns {number} - Interval ID for cleanup
 */
const startInventoryPolling = (businessId, onStockAlert, interval = 30000) => {
  if (!businessId || typeof onStockAlert !== 'function') {
    console.error('[InventoryNotificationService] Invalid parameters');
    return null;
  }

  // Stop any existing polling for this business
  stopInventoryPolling(businessId);

  // Store callback
  state.callbacks.set(businessId, onStockAlert);

  // Initial check to set baseline
  checkInventory(businessId, onStockAlert, true);

  // Start polling
  const intervalId = setInterval(() => {
    // Only poll if page is visible
    if (document.visibilityState === 'visible') {
      checkInventory(businessId, onStockAlert, false);
    }
  }, interval);

  state.pollingIntervals.set(businessId, intervalId);
  
  console.log(`[InventoryNotificationService] Started polling for business: ${businessId}`);
  
  return intervalId;
};

/**
 * Stop polling for a specific business
 * @param {string} businessId - Business ID to stop polling for
 */
const stopInventoryPolling = (businessId) => {
  const intervalId = state.pollingIntervals.get(businessId);
  
  if (intervalId) {
    clearInterval(intervalId);
    state.pollingIntervals.delete(businessId);
    state.callbacks.delete(businessId);
    console.log(`[InventoryNotificationService] Stopped polling for business: ${businessId}`);
  }
};

/**
 * Stop all polling
 */
const stopAllPolling = () => {
  state.pollingIntervals.forEach((intervalId, businessId) => {
    clearInterval(intervalId);
    console.log(`[InventoryNotificationService] Stopped polling for business: ${businessId}`);
  });
  
  state.pollingIntervals.clear();
  state.lastStockAlerts.clear();
  state.callbacks.clear();
  
  console.log('[InventoryNotificationService] Stopped all polling');
};

/**
 * Get current stock alerts for a business
 * @param {string} businessId - Business ID
 * @returns {Array} - Array of stock alerts
 */
const getCurrentAlerts = (businessId) => {
  return state.lastStockAlerts.get(businessId) || [];
};

/**
 * Get critical alerts only
 * @param {string} businessId - Business ID
 * @returns {Array} - Array of critical alerts
 */
const getCriticalAlerts = (businessId) => {
  const alerts = state.lastStockAlerts.get(businessId) || [];
  return alerts.filter(a => a.severity === 'critical');
};

/**
 * Get warning alerts only
 * @param {string} businessId - Business ID
 * @returns {Array} - Array of warning alerts
 */
const getWarningAlerts = (businessId) => {
  const alerts = state.lastStockAlerts.get(businessId) || [];
  return alerts.filter(a => a.severity === 'warning');
};

/**
 * Get alert count for a business
 * @param {string} businessId - Business ID
 * @returns {Object} - Alert counts by severity
 */
const getAlertCount = (businessId) => {
  const alerts = state.lastStockAlerts.get(businessId) || [];
  return {
    total: alerts.length,
    critical: alerts.filter(a => a.severity === 'critical').length,
    warning: alerts.filter(a => a.severity === 'warning').length
  };
};

/**
 * Clear alerts for a business
 * @param {string} businessId - Business ID
 */
const clearAlerts = (businessId) => {
  state.lastStockAlerts.delete(businessId);
  console.log(`[InventoryNotificationService] Cleared alerts for business: ${businessId}`);
};

/**
 * Check if polling is active for a business
 * @param {string} businessId - Business ID
 * @returns {boolean} - True if polling is active
 */
const isPollingActive = (businessId) => {
  return state.pollingIntervals.has(businessId);
};

/**
 * Get all active polling business IDs
 * @returns {Array<string>} - Array of business IDs
 */
const getActivePollingBusinesses = () => {
  return Array.from(state.pollingIntervals.keys());
};

/**
 * Manually trigger an inventory check (useful for testing)
 * @param {string} businessId - Business ID
 */
const triggerManualCheck = async (businessId) => {
  const callback = state.callbacks.get(businessId);
  if (callback) {
    await checkInventory(businessId, callback, false);
  } else {
    console.warn(`[InventoryNotificationService] No callback found for business: ${businessId}`);
  }
};

/**
 * Get service statistics
 * @returns {Object} - Service statistics
 */
const getStats = () => {
  return {
    activePolling: state.pollingIntervals.size,
    trackedBusinesses: state.lastStockAlerts.size,
    totalAlerts: Array.from(state.lastStockAlerts.values()).reduce((sum, alerts) => sum + alerts.length, 0),
    businesses: Array.from(state.lastStockAlerts.entries()).map(([id, alerts]) => ({
      businessId: id,
      alertCount: alerts.length,
      criticalCount: alerts.filter(a => a.severity === 'critical').length,
      warningCount: alerts.filter(a => a.severity === 'warning').length,
      isPolling: state.pollingIntervals.has(id)
    }))
  };
};

// Export service functions as a single object
const inventoryNotificationService = {
  startInventoryPolling,
  stopInventoryPolling,
  stopAllPolling,
  getCurrentAlerts,
  getCriticalAlerts,
  getWarningAlerts,
  getAlertCount,
  clearAlerts,
  isPollingActive,
  getActivePollingBusinesses,
  triggerManualCheck,
  getStats
};

export default inventoryNotificationService;
